import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { APP_VERSION } from "../../utility/keys";
import { preprocessAccessGates, resolveInternalSlug } from "./manualIndex";

// - - - - - - - - - -
// ManualContent — fetches a single markdown page from the static-asset
// path served out of public/manual/ (populated by scripts/build-manual.js
// at prestart/prebuild) and renders it via react-markdown.
//
// Cache-busting: every fetch URL carries `?v=<APP_VERSION>` so a deployed
// build always serves its own manual content. Without this, a returning
// user's browser cache could serve stale markdown after a release.
// (Plan gotcha #6.)
//
// Internal links: react-markdown's default <a> renderer does a real
// browser navigation, which closes HelpModal and reloads the SPA when an
// author writes "[Foo](foo.md)" inside a manual page. Plan gotcha #8.
// We override the `a` component to detect internal manual links via
// resolveInternalSlug and call onNavigate(slug) instead. External links
// (http/https/mailto) keep the default behavior with target="_blank".

function ManualContent({ page, pages, onNavigate, hiddenGroups }) {
  const [state, setState] = useState({ status: "loading", markdown: "" });

  const components = useMemo(
    () => ({
      img: ({ src, alt, title }) => {
        // Manual images live under public/manual/<lang>/manual-images/...
        // and are referenced from MD as relative paths like
        // "manual-images/orientation/schedule-grid.png". react-markdown
        // passes whatever src the author wrote; the browser resolves it
        // against the current document URL (which is "/" — the SPA root).
        // We need an absolute path so the browser fetches from the right
        // place. If the author wrote a relative path, prepend the active
        // language's manual root.
        const isAbsolute =
          typeof src === "string" &&
          (/^https?:\/\//i.test(src) || src.startsWith("/"));
        const resolvedSrc = isAbsolute
          ? src
          : `/manual/en/${src}`;
        const altText = alt || "";
        return (
          <a
            href={resolvedSrc}
            target="_blank"
            rel="noopener noreferrer"
            title={title || `Open ${altText || "image"} at full size`}
            // max-w-[50%] caps the rendered image at half the manual
            // pane's width on big screens; on narrow screens it just
            // takes whatever pane width is available (still capped).
            // Click anywhere on the image opens the full-resolution PNG
            // in a new tab — easy to dial down further (40%, 33%, etc.)
            // if 50% is still too big once we have multiple images on
            // a single page.
            className="block my-3 max-w-[50%]"
          >
            <img
              src={resolvedSrc}
              alt={altText}
              // max-h-[60vh] caps tall portrait images (e.g. the
              // history-modal captures, which are ~1.5x taller than
              // wide) at 60% of viewport height so they don't dominate
              // the manual pane vertically. Wide images (e.g. the
              // sepia-banner crop, or full-grid screenshots) hit
              // max-w-full first and are unaffected. w-auto/h-auto +
              // max-w-full preserves natural aspect ratio rather than
              // stretching to fill a fixed box.
              className="block max-w-full max-h-[60vh] w-auto h-auto rounded-sm border border-skin-base shadow-xs cursor-zoom-in"
            />
          </a>
        );
      },
      a: ({ href, children, ...rest }) => {
        const slug = resolveInternalSlug(href, pages);
        if (slug && typeof onNavigate === "function") {
          return (
            <a
              href={`#${slug}`}
              data-manual-internal-link={slug}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(slug);
              }}
              className="text-skin-link hover:underline"
            >
              {children}
            </a>
          );
        }
        // External absolute URLs open in a new tab. This MUST be a real
        // remote URL — never fall through to the raw href for relative
        // paths, which would let the browser navigate to e.g.
        // /getting-started.md and trip MSAL's redirect-URI check.
        const isExternalUrl =
          typeof href === "string" &&
          (/^https?:\/\//i.test(href) ||
            /^mailto:/i.test(href) ||
            /^tel:/i.test(href));
        if (isExternalUrl) {
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-skin-link hover:underline"
              {...rest}
            >
              {children}
            </a>
          );
        }
        // Anything else is a broken or stale relative reference. Render
        // the text as a non-navigating span so the browser never tries
        // to follow it; the data-broken-link attribute is there for
        // diagnostics / dev-tools inspection.
        return (
          <span
            data-broken-link={href}
            title={`Broken manual link: ${href}`}
            className="text-skin-link underline cursor-help"
          >
            {children}
          </span>
        );
      },
      // Typography renderers — give sections clear visual separation.
      // Markdown's blank lines between blocks all collapse into a
      // single space at the HTML level; what actually creates visible
      // breathing room is per-element margin. Without these renderers,
      // the default browser style renders everything stacked tight.
      h1: ({ children }) => (
        <h1 className="text-2xl font-bold mb-4">{children}</h1>
      ),
      h2: ({ children }) => (
        <h2 className="text-xl font-bold mt-8 mb-3">{children}</h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-lg font-semibold mt-5 mb-2">{children}</h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-base font-semibold mt-4 mb-2">{children}</h4>
      ),
      p: ({ children }) => (
        <p className="mb-3 leading-relaxed">{children}</p>
      ),
      ul: ({ children }) => (
        <ul className="list-disc pl-6 mb-3 leading-relaxed">{children}</ul>
      ),
      ol: ({ children }) => (
        <ol className="list-decimal pl-6 mb-3 leading-relaxed">{children}</ol>
      ),
      li: ({ children }) => <li className="mb-1">{children}</li>,
      hr: () => <hr className="my-6 border-skin-base" />,
      blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-skin-base pl-4 italic my-3 opacity-90">
          {children}
        </blockquote>
      ),
      table: ({ children }) => (
        <table className="border-collapse my-3 w-full">{children}</table>
      ),
      thead: ({ children }) => (
        <thead className="border-b-2 border-skin-base">{children}</thead>
      ),
      tr: ({ children }) => (
        <tr className="border-b border-skin-base">{children}</tr>
      ),
      th: ({ children }) => (
        <th className="text-left font-semibold px-2 py-1">{children}</th>
      ),
      td: ({ children }) => <td className="px-2 py-1 align-top">{children}</td>,
      code: ({ children, className: codeClassName }) => {
        // react-markdown 10.x removed the `inline` prop on the code
        // component, so we have to detect inline-vs-block ourselves.
        // Reliable heuristic: triple-backtick code blocks contain
        // newlines; inline code (single backticks) doesn't. Fenced code
        // blocks usually also carry a `language-...` className when a
        // language was specified.
        const childString = Array.isArray(children)
          ? children.join("")
          : String(children ?? "");
        const isBlock =
          childString.includes("\n") ||
          (typeof codeClassName === "string" &&
            codeClassName.startsWith("language-"));
        return isBlock ? (
          <pre className="bg-skin-base p-3 rounded-sm my-3 overflow-x-auto font-mono text-xs">
            <code className={codeClassName}>{children}</code>
          </pre>
        ) : (
          <code className="px-1 rounded-sm font-mono text-xs bg-skin-base">
            {children}
          </code>
        );
      },
    }),
    [pages, onNavigate]
  );

  useEffect(() => {
    if (!page) {
      setState({ status: "empty", markdown: "" });
      return undefined;
    }

    let cancelled = false;
    setState({ status: "loading", markdown: "" });

    const url = `${page.path}?v=${encodeURIComponent(APP_VERSION)}`;
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then((markdown) => {
        if (!cancelled) {
          setState({ status: "ready", markdown });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: "error", markdown: "" });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page]);

  if (state.status === "empty") {
    return (
      <div data-testid="manual-content-empty" className="opacity-70">
        Pick a topic from the left to begin.
      </div>
    );
  }

  if (state.status === "loading") {
    return (
      <div data-testid="manual-content-loading" className="opacity-70">
        Loading…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div data-testid="manual-content-error" className="text-red">
        Could not load this manual page. Try again, or pick a different topic.
      </div>
    );
  }

  // Strip access-gated content (e.g. <!--access:time-travel-->...
  // <!--/access:time-travel-->) for users in hiddenGroups, so MD pages
  // that mention an access-gated feature stop mentioning it for users
  // without that access. Same hiddenGroups list that hides the nav
  // chapter, so they stay in lockstep.
  const renderableMarkdown = preprocessAccessGates(
    state.markdown,
    hiddenGroups
  );

  return (
    <div
      data-testid="manual-content-rendered"
      className="prose prose-sm max-w-none leading-relaxed"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {renderableMarkdown}
      </ReactMarkdown>
    </div>
  );
}

export default ManualContent;
